/**
 * How big a player's body is THIS tick.
 *
 * `PLAYER_RADIUS` and `PLAYER_HALF_HEIGHT` used to be read directly by
 * collision and combat, which made the body a compile-time constant. A mutator
 * that changes it has to be read at the same moment the hitbox is tested, or
 * the body you can see and the body you can hit stop being the same object —
 * shots passing through a giant is the exact bug that makes a size mutator feel
 * broken rather than chaotic.
 *
 * WHY THIS IS ITS OWN MODULE. `mutations.ts` imports `combat.ts` (chaos damage
 * goes through `applyDamage`), so `combat.ts` cannot import back from it. This
 * file imports nothing at runtime except constants, so both sides can read it.
 * `isMutationActive` lives here for the same reason and is imported BY
 * `mutations.ts` rather than copied — one definition of "in effect".
 *
 * DERIVED, NEVER STORED. The scale is recomputed from `world.chaos.applied` on
 * every read rather than cached on the world or the player. `applied` is
 * already in the serialization and the checksum, so a cache would be a second
 * copy of a number the digest already commits to — and a second copy that can
 * disagree is how a hitbox drifts from its body.
 */
import { PLAYER_HALF_HEIGHT, PLAYER_RADIUS } from "./constants.js";
import { getCharacter } from "@chaos/protocol";
/** In effect on `tick`. One-shot mutations have `untilTick === tick` and are never active. */
export function isMutationActive(entry, tick) {
    return tick >= entry.tick && tick < entry.untilTick;
}
/**
 * Body scale from active mutations. 1 = normal.
 *
 * FURTHEST FROM 1 WINS, rather than multiplying: two overlapping rolls of 130%
 * must not compound into 169%, which is a body wider than the gaps it has to
 * walk through. Same stance `chaosGravityMultiplier` takes, for the same
 * reason — overlapping windows are a scheduling detail, not a design.
 */
export function chaosPlayerScale(world) {
    let scale = 1;
    for (const entry of world.chaos.applied) {
        const m = entry.mutation;
        if (m.kind !== "player_scale" || !isMutationActive(entry, world.tick))
            continue;
        const v = m.scalePct / 100;
        if (Math.abs(v - 1) > Math.abs(scale - 1))
            scale = v;
    }
    return scale;
}
/** The radius collision and every hitbox test must use. */
export function bodyRadius(world) {
    return PLAYER_RADIUS * chaosPlayerScale(world);
}
/** Half the body's height. Feet are at `y - bodyHalfHeight(world)`. */
export function bodyHalfHeight(world) {
    return PLAYER_HALF_HEIGHT * chaosPlayerScale(world);
}
/**
 * Any player by id, alive or not. Here rather than in `combat.ts` for the
 * reason at the top of this file: `loot.ts` needs it, and `combat.ts` imports
 * `loot.ts`. `combat.ts` re-exports it, so every existing caller is unchanged.
 */
export function findPlayer(world, id) {
    for (const p of world.players)
        if (p.id === id)
            return p;
    return null;
}
/** The one cap every heal and every health bar measures against. */
export function maxHealth(p) {
    return getCharacter(p.characterId).maxHealth;
}
//# sourceMappingURL=body.js.map